import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "tss-react/mui";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/actions";

import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import AuthProviderService from "../../service/authProvider";
import AddForm from "./SAMLProvider/AddForm";
import AddOIDCForm from "./OIDCProvider/AddForm";
import { BigTile, TextComponent } from "../Common/Tile";
import { ButtonUI, ConfirmPopup } from "../Common";
import CommonLayout from "../Common/Layout";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  paper: {
    padding: "16px",
    textAlign: "center",
    backgroundColor: theme.palette.success.light,
    color: theme.palette.secondary.text,
    height: 200,
    lineHeight: "60px",
    position: "relative",
    marginBottom: 20,
    userSelect: "none",
    background: "linear-gradient(60deg,#a5d6a7,#66bb6a)",
    border: "1px solid #ACCD99",
    "&.error": {
      background: "linear-gradient(60deg,#ef9a9a,#f44336)",
      border: "1px solid #DCABAA",
    },
    "&.loading": {
      background: "linear-gradient(60deg,#f5f5f5,#f5f5f5)",
      border: "1px solid #f5f5f5",
    },
  },
  skeleton: {
    borderRadius: "5px",
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    width: "80%",
    height: 24,
  },
  boxActionContainer: {
    height: "auto",
    margin: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  boxActionContainerType: {
    marginTop: "10px",
    marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
  },
  boxActionContainerStatus: {
    marginTop: "10px",
    marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
  },
  boxActionContainerLink: {
    position: "absolute",
    bottom: 10,
    width: "calc(100% - 10%)",
    display: "flex",
    justifyContent: "center",
    "& Button": {
      fontWeight: "600",
      textTransform: "capitalize",
      fontSize: "medium",
    },
  },
}));

const AuthProvider = () => {
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [openSAML, setOpenSAML] = useState(false);
  const [openOIDC, setOpenOIDC] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [providerList, setProviderList] = useState([]);
  const [idpId, setIdpId] = useState(0);
  const [idpName, setIdpName] = useState("");
  const [editScrollHeight, setEditScrollHeight] = useState(0);

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const loadIDPData = useCallback(
    async (loadAfter = "") => {
      setLoading(true);

      const authProviderService = new AuthProviderService();
      const result = await authProviderService.getList();

      if (result.success && result.data && Object.keys(result.data).length > 0) {
        setProviderList(result.data);
        setTimeout(() => {
          if (loadAfter === "add" || loadAfter === "edit") {
            document.getElementsByTagName("main")[0].scrollTo({
              top: loadAfter === "add" ? document.getElementsByTagName("main")[0].scrollHeight : editScrollHeight,
              left: 0,
              behavior: "smooth",
            });
          }
        }, 0);
      }

      setLoading(false);
    },
    [editScrollHeight]
  );

  const deleteIDP = async (IDP_Id) => {
    setLoadingDelete(true);

    const authProviderService = new AuthProviderService();
    const result = await authProviderService.deleteIDP(IDP_Id);

    if (result.success) {
      loadIDPData();

      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
    }
    setOpenDelete(false);

    setLoadingDelete(false);
  };

  const handleCancel = () => {
    setOpenDelete(false);
  };

  useEffect(() => {
    loadIDPData();
  }, [loadIDPData]);

  const breadcrumbArr = [
    { link: "#", displayName: "Settings", active: true },
    { link: "#", displayName: "SSO Settings", active: true },
  ];

  const ActionGrid = () => (
    <Grid container spacing={3}>
      {providerList.map((row, i) => {
        const chips = [
          {
            text: row.Status === 1 ? "Enabled" : "Disabled",
            chipTextColor: row.Status === 1 ? "#20732C" : "#ef5350",
            chipBGColor: row.Status === 1 ? "#CAF2C7" : "#ffcdd2",
          },
        ];

        if (row.Name === "Built In")
          chips.push({
            text: "MFA",
          });

        return (
          <Grid
            key={i}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: 4,
              xl: 3,
            }}
          >
            <BigTile
              configData={{
                dataId: "ssoSet" + row.Name.replace(/\s/g, ""),
                editIcon: (
                  <Box>
                    {row.IDP_id === 1 ? (
                      ""
                    ) : (
                      <IconButton
                        aria-label="edit"
                        disabled={row.IDP_id === 1}
                        onClick={(e) => {
                          setEditScrollHeight(e.target.scrollHeight + 200);
                          setIdpId(row.IDP_id);
                          row.Type === "SAML" ? setOpenSAML(true) : setOpenOIDC(true);
                        }}
                      >
                        <EditOutlinedIcon color={"primary"} />
                      </IconButton>
                    )}
                  </Box>
                ),
                deleteIcon: (
                  <Box>
                    {row.Status === 0 ? (
                      <IconButton
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setIdpName(row.Name);
                          setIdpId(row.IDP_id);
                          setOpenDelete(true);
                        }}
                      >
                        <DeleteOutlineOutlinedIcon color={"red"} />
                      </IconButton>
                    ) : (
                      ""
                    )}
                  </Box>
                ),

                borderLeft: row.Status === 0 ? "10px solid #ef5350" : "10px solid #20732C",
                label: row.Name,
                description: <TextComponent label="" value={row.Type} style={{ marginTop: "10px" }} />,
                chips,
              }}
            />
          </Grid>
        );
      })}
    </Grid>
  );

  // Showing Empty Grid when Loading
  const LoadingGrid = () => {
    return (
      <Grid container spacing={3} sx={{ height: "100%" }}>
        {[0, 1, 2].map((v, i) => (
          <Grid
            key={i}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            <Paper elevation={1} className={`${classes.paper} loading`} sx={{ borderRadius: 4 }}>
              <Box className={classes.boxActionContainer}>
                <Skeleton className={classes.skeleton} variant="rectangular" />
              </Box>
              <Divider />
              <Box className={classes.boxActionContainerType}>
                <Skeleton className={classes.skeleton} variant="rectangular" />
              </Box>
              <Box className={classes.boxActionContainerStatus}>
                <Skeleton className={classes.skeleton} variant="rectangular" />
              </Box>

              <Box className={classes.boxActionContainerLink} sx={{ mb: 2 }}>
                <Skeleton className={classes.skeleton} variant="rectangular" />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Open Delete Confirm Popup
  const DeletePopup = ({ IDP_Id }) => {
    return (
      <ConfirmPopup
        configData={{
          popupTitle: "Delete Identity Provider",
          popupMessage: `Are you sure you want to delete "${idpName}"?`,
          handleCancel: handleCancel,
          handleClick: () => deleteIDP(IDP_Id),
          loadingForConfirm: loadingDelete,
        }}
      />
    );
  };

  return (
    <CommonLayout title="SSO Settings" loading={loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          paddinBotton: "80px",
        }}
      >
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Grid container spacing={5} direction="row" alignItems={"center"}>
              <Grid
                size={{
                  md: 12,
                  sm: 10,
                }}
              >
                <Box sx={{ display: "inline-flex", paddingTop: "5px" }}>
                  <ButtonUI
                    data-id="ssoSetAddSAMLProvider"
                    endIcon={<AddIcon />}
                    variant="contained"
                    disabled={loading}
                    color="info"
                    style={{ width: "auto" }}
                    onClick={() => {
                      setIdpId(0);
                      setOpenSAML(true);
                    }}
                  >
                    Add SAML Provider
                  </ButtonUI>
                  <ButtonUI
                    data-id="ssoSetAddOIDCProvider"
                    endIcon={<AddIcon />}
                    variant="contained"
                    disabled={loading}
                    color="info"
                    style={{ marginLeft: "15px", width: "auto" }}
                    onClick={() => {
                      setIdpId(0);
                      setOpenOIDC(true);
                    }}
                  >
                    Add OIDC Provider
                  </ButtonUI>
                </Box>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            padding: "10px 20px 0px 20px",
          }}
        >
          {loading ? <LoadingGrid /> : <ActionGrid />}
        </Box>
      </Box>
      {openSAML && <AddForm setOpen={setOpenSAML} IDP_Id={idpId} loadIDPData={loadIDPData} />}
      {openOIDC && <AddOIDCForm setOpen={setOpenOIDC} IDP_Id={idpId} loadIDPData={loadIDPData} />}
      {openDelete && <DeletePopup IDP_Id={idpId} />}
    </CommonLayout>
  );
};

export default AuthProvider;
